/**
 * AdminDashboard (aka Owner / CEO view).
 *
 * 12 static widgets in a 12-col grid. Based on `OwnerDashboard` from
 * /tmp/aura-design/extracted/aura-web-app/project/aura-dash-1.jsx (lines 11–85).
 *
 * Drag-reorder is DEFERRED — the design spec shows it, but for this iteration
 * we ship a static grid and layer reorder in a later pass.
 *
 * UI-only. Calls existing endpoints. Zero backend changes.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Download, Sparkles, X, Check, ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { formatINRCompact, splitINRCompact, formatNum, safeGet } from './_dashboardShared';

/* ─── Date-range filter ─────────────────────────────────────────────────
 * Apr 24, 2026 (TM) — the "This month" button was previously inert. It now
 * drives a state-driven date-range selector that widgets may consume via an
 * optional `dateRange` prop. Widgets that don't accept the prop are
 * unaffected (existing empty-state / "No data yet" behaviour still applies).
 * Selection is persisted per-user in localStorage. "Custom range" is stubbed
 * for this batch — clicking it shows a "Coming soon" toast.
 */
export type AdminDashboardDateRange =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'fy_to_date'
  | 'custom';

const DATE_RANGE_STORAGE_KEY = 'aura-admin-date-range';

const DATE_RANGE_OPTIONS: { id: AdminDashboardDateRange; label: string }[] = [
  { id: 'this_month',   label: 'This month' },
  { id: 'last_month',   label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'fy_to_date',   label: 'FY to date' },
  { id: 'custom',       label: 'Custom range' },
];

function loadDateRange(): AdminDashboardDateRange {
  if (typeof window === 'undefined') return 'this_month';
  const raw = window.localStorage.getItem(DATE_RANGE_STORAGE_KEY);
  const valid: AdminDashboardDateRange[] = ['this_month', 'last_month', 'this_quarter', 'fy_to_date', 'custom'];
  if (raw && (valid as string[]).includes(raw)) return raw as AdminDashboardDateRange;
  return 'this_month';
}

function saveDateRange(r: AdminDashboardDateRange) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(DATE_RANGE_STORAGE_KEY, r); } catch { /* quota — ignore */ }
}

function labelForRange(r: AdminDashboardDateRange): string {
  return DATE_RANGE_OPTIONS.find((o) => o.id === r)?.label ?? 'This month';
}

// ─── Types ──────────────────────────────────────────────────────────────

interface KpiPayload {
  value?: number | string | null;
  delta?: number;
  trend?: Array<{ t: string; v: number }>;
  breakdown?: Array<{ label: string; value: number }>;
  meta?: Record<string, unknown>;
}

interface OwnerAggregate {
  pipeline?: { total?: number; count?: number };
  invoices?: { total?: number; amountDue?: number; amountPaid?: number };
  cash?: { totalCollected?: number };
  overdueInvoices?: Array<{ id: string; invoiceNumber: string; account?: { name?: string }; total?: number; daysOverdue?: number }>;
  revenue?: { mtd?: number; lastMonth?: number; growth?: number };
}

interface SoPoStats {
  green?: number;
  yellow?: number;
  red?: number;
  total?: number;
  mapped?: number;
  avgMappingHours?: number;
}

interface ArAging {
  buckets?: Array<{ label: string; value: number }>;
  total?: number;
  invoiceCount?: number;
}

interface TopDealsPayload {
  deals?: Array<{
    id: string;
    name?: string;
    value?: number;
    probability?: number;
    owner?: { name?: string };
    stage?: { name?: string };
    expectedCloseDate?: string;
  }>;
}

// ─── Small visual atoms (inline, token-driven) ───────────────────────────

function Progress({ value, color = 'var(--forest-600)', h = 4 }: { value: number; color?: string; h?: number }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ height: h, background: 'var(--ivory-3)', borderRadius: h }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: h, transition: 'width .3s' }} />
    </div>
  );
}

function Donut({
  segments,
  size = 90,
  thickness = 14,
  label,
}: {
  segments: Array<{ value: number; color: string }>;
  size?: number;
  thickness?: number;
  label?: React.ReactNode;
}) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--ivory-3)" strokeWidth={thickness} fill="none" />
        {segments.map((s, i) => {
          const len = (s.value / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke={s.color}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {label}
      </div>
    </div>
  );
}

// ─── Card wrappers ───────────────────────────────────────────────────────

function WidgetShell({ cols = 4, children }: { cols?: number; children: React.ReactNode }) {
  return (
    <div style={{ gridColumn: `span ${cols}` }}>
      <div className="card" style={{ padding: 18, height: '100%' }}>
        {children}
      </div>
    </div>
  );
}

function Skeleton({ h = 80 }: { h?: number }) {
  return <div style={{ height: h, background: 'var(--ivory-2)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />;
}

function EmptyChip() {
  return <span className="chip" style={{ fontSize: 11 }}>Unavailable</span>;
}

function NoData() {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>No data yet</div>;
}

// ─── Widgets ────────────────────────────────────────────────────────────

function RevenueMtdWidget() {
  const [d, setD] = useState<OwnerAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<OwnerAggregate>('/analytics/owner-dashboard').then((r) => {
      setD(r.data);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={90} /></WidgetShell>;

  const mtd = d?.revenue?.mtd ?? d?.invoices?.amountPaid ?? null;
  const last = d?.revenue?.lastMonth ?? null;
  const growth = d?.revenue?.growth ?? (mtd && last ? ((mtd - last) / last) * 100 : null);
  const { num, unit } = splitINRCompact(mtd);

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 6 }}>Revenue · MTD</div>
      {mtd != null ? (
        <>
          <div className="num-display" style={{ fontSize: 32 }}>
            {num}
            {unit && <span style={{ fontSize: 20, color: 'var(--ink-3)' }}> {unit}</span>}
          </div>
          {growth != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 4 }}>
              <span style={{ color: growth >= 0 ? 'var(--forest-600)' : 'var(--danger)' }}>
                {growth >= 0 ? '↑' : '↓'} {Math.abs(growth).toFixed(1)}%
              </span>
              {last != null && <span style={{ color: 'var(--ink-3)' }}>vs {formatINRCompact(last)} last month</span>}
            </div>
          )}
        </>
      ) : error ? <EmptyChip /> : <NoData />}
    </WidgetShell>
  );
}

function PipelineValueWidget() {
  const [d, setD] = useState<OwnerAggregate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<OwnerAggregate>('/analytics/owner-dashboard').then((r) => {
      setD(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={90} /></WidgetShell>;

  const total = d?.pipeline?.total ?? null;
  const count = d?.pipeline?.count ?? 0;
  const avg = total && count ? total / count : null;
  const { num, unit } = splitINRCompact(total);

  // Apr 23, 2026 (Bug 6) — surface an actionable empty state instead of "₹0"
  // when there's no pipeline data yet. Happens for brand-new tenants like
  // Sun Stellar which still has 0 deals post-migration.
  const isEmpty = total == null || (total === 0 && count === 0);

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 6 }}>Pipeline · Weighted</div>
      {isEmpty ? (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>
          No deals in pipeline.{' '}
          <a href="/deals" style={{ color: 'var(--saffron-600)', textDecoration: 'underline' }}>
            Create one
          </a>.
        </div>
      ) : (
        <>
          <div className="num-display" style={{ fontSize: 32 }}>
            {num}
            {unit && <span style={{ fontSize: 20, color: 'var(--ink-3)' }}> {unit}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            {count} deals{avg != null ? ` · avg ${formatINRCompact(avg)}` : ''}
          </div>
        </>
      )}
    </WidgetShell>
  );
}

function CashPositionWidget() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/finance/cash-position').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={90} /></WidgetShell>;

  const total = typeof data?.value === 'number' ? data.value : null;
  const accounts = (data?.breakdown ?? []) as Array<{ label: string; value: number }>;
  const { num, unit } = splitINRCompact(total);

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 6 }}>Cash position</div>
      {total != null ? (
        <>
          <div className="num-display" style={{ fontSize: 32 }}>
            {num}
            {unit && <span style={{ fontSize: 20, color: 'var(--ink-3)' }}> {unit}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>Across {accounts.length || '—'} bank accounts</div>
          {accounts.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {accounts.slice(0, 3).map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: 12 }}>
                  <span style={{ flex: 1, color: 'var(--ink-2)' }}>{r.label}</span>
                  <span className="num" style={{ fontWeight: 500 }}>{formatINRCompact(r.value)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : <NoData />}
    </WidgetShell>
  );
}

function ArAgingWidget() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/finance/receivables-aging').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={100} /></WidgetShell>;

  const total = typeof data?.value === 'number' ? data.value : null;
  const buckets = (data?.breakdown ?? []) as Array<{ label: string; value: number }>;
  const invoiceCount = (data?.meta?.invoiceCount as number) ?? null;
  const { num, unit } = splitINRCompact(total);

  // Default order: 0-30, 31-60, 61-90, 90+
  const order = ['0-30', '31-60', '61-90', '90+'];
  const colors = ['var(--forest)', 'var(--saffron)', '#C67C1B', 'var(--danger)'];
  const mapped = order.map((label) => {
    const b = buckets.find((x) => x.label?.includes(label) || x.label?.replace('d', '').includes(label));
    return b?.value ?? 0;
  });
  const sum = mapped.reduce((a, b) => a + b, 0) || 1;

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 6 }}>AR Aging</div>
      {total != null ? (
        <>
          <div className="num-display" style={{ fontSize: 32 }}>
            {num}
            {unit && <span style={{ fontSize: 20, color: 'var(--ink-3)' }}> {unit}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            {invoiceCount != null ? `Outstanding across ${invoiceCount} invoices` : 'Outstanding receivables'}
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 2, height: 30, borderRadius: 4, overflow: 'hidden' }}>
            {mapped.map((v, i) => (
              <div key={i} style={{ flex: Math.max(v / sum, 0.01), background: colors[i] }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-3)', marginTop: 6 }}>
            {order.map((label, i) => (
              <span key={i} style={i === 3 ? { color: 'var(--danger)' } : undefined}>
                {label} {sum > 0 ? Math.round((mapped[i]! / sum) * 100) : 0}%
              </span>
            ))}
          </div>
        </>
      ) : <NoData />}
    </WidgetShell>
  );
}

function TopDealsWidget() {
  const [data, setData] = useState<TopDealsPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<TopDealsPayload>('/analytics/sales/top-deals-closing?limit=5').then((r) => {
      // role-kpis wraps in { value, breakdown }. Try both shapes.
      const raw = r.data as unknown as { deals?: TopDealsPayload['deals']; breakdown?: Array<{ label: string; value: number }> } | null;
      const deals = raw?.deals
        ?? (raw?.breakdown ?? []).map((b) => ({
          id: b.label,
          name: b.label,
          value: b.value,
          probability: 50,
        }));
      setData({ deals });
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={6}><Skeleton h={160} /></WidgetShell>;

  const deals = data?.deals ?? [];

  return (
    <WidgetShell cols={6}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div className="kpi-label">Top deals this month</div>
      </div>
      {deals.length > 0 ? (
        <div>
          {deals.slice(0, 5).map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < Math.min(4, deals.length - 1) ? '1px solid var(--ivory-3)' : 'none' }}>
              <div style={{ width: 28, textAlign: 'right' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--ink-3)' }}>#{i + 1}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name ?? 'Unnamed'}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {d.owner?.name ?? '—'}{d.expectedCloseDate ? ` · close ${new Date(d.expectedCloseDate).toLocaleDateString('en-IN')}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 50 }}><Progress value={d.probability ?? 0} color="var(--ink-0)" h={3} /></div>
                <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)', width: 28 }}>{d.probability ?? 0}%</span>
              </div>
              <div className="num-display" style={{ fontSize: 16, width: 76, textAlign: 'right' }}>{formatINRCompact(d.value)}</div>
            </div>
          ))}
        </div>
      ) : <NoData />}
    </WidgetShell>
  );
}

function SoPoDonutWidget() {
  const [data, setData] = useState<SoPoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    safeGet<SoPoStats>('/so-po-mapping/kpis').then((r) => {
      setData(r.data);
      setError(r.error);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={120} /></WidgetShell>;

  const green = data?.green ?? 0;
  const yellow = data?.yellow ?? 0;
  const red = data?.red ?? 0;
  const total = green + yellow + red;
  const mappedPct = total > 0 ? Math.round((green / total) * 100) : 0;

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>SO → PO mapping</div>
      {total > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Donut
            size={92}
            thickness={14}
            segments={[
              { value: green, color: 'var(--forest)' },
              { value: yellow, color: '#C67C1B' },
              { value: red, color: 'var(--danger)' },
            ]}
            label={
              <>
                <div className="num-display" style={{ fontSize: 20 }}>{mappedPct}%</div>
                <div style={{ fontSize: 9, color: 'var(--ink-3)' }}>mapped</div>
              </>
            }
          />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--forest)' }} />
              <span style={{ flex: 1 }}>&lt;24h</span>
              <span className="num">{green}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: '#C67C1B' }} />
              <span style={{ flex: 1 }}>24–48h</span>
              <span className="num">{yellow}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--danger)' }} />
              <span style={{ flex: 1 }}>&gt;48h</span>
              <span className="num">{red}</span>
            </div>
          </div>
        </div>
      ) : error ? <EmptyChip /> : <NoData />}
    </WidgetShell>
  );
}

function AiSummaryWidget() {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);

  useEffect(() => {
    safeGet<{ summary?: string; generatedAt?: string }>('/ai/weekly-summary/latest').then((r) => {
      setSummary(r.data?.summary ?? null);
      setGeneratedAt(r.data?.generatedAt ?? null);
      setLoading(false);
    });
  }, []);

  return (
    <WidgetShell cols={6}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkles size={14} color="var(--saffron-600)" />
        <div className="kpi-label" style={{ color: 'var(--saffron-600)' }}>Weekly AI summary</div>
        <span className="chip chip-amber">5 credits</span>
        <div style={{ flex: 1 }} />
        {generatedAt && (
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Generated {new Date(generatedAt).toLocaleDateString('en-IN')}
          </span>
        )}
      </div>
      {loading ? <Skeleton h={60} /> : summary ? (
        <>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--ink-1)', marginBottom: 12 }}>{summary}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Apr 27 (audit P1) — wire AI summary buttons to AI panel via global event. */}
            <button
              className="btn btn-outline btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'weekly_summary_full' } }))}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Read full summary
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'weekly_summary_regenerate' } }))}
              style={{ fontSize: 12, padding: '4px 10px' }}
            >
              Regenerate · 5 cr
            </button>
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No summary generated yet.{' '}
          <button
            className="btn btn-outline btn-sm"
            onClick={() => window.dispatchEvent(new CustomEvent('aura-ai-open', { detail: { preset: 'weekly_summary_generate' } }))}
            style={{ fontSize: 12, padding: '4px 10px', marginLeft: 8 }}
          >
            Generate · 5 cr
          </button>
        </div>
      )}
    </WidgetShell>
  );
}

function OverdueInvoicesWidget() {
  const [d, setD] = useState<OwnerAggregate | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<OwnerAggregate>('/analytics/owner-dashboard').then((r) => {
      setD(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={4}><Skeleton h={120} /></WidgetShell>;

  const invoices = d?.overdueInvoices ?? [];

  return (
    <WidgetShell cols={4}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div className="kpi-label">Overdue invoices</div>
        <span className="chip chip-red" style={{ marginLeft: 8 }}>{invoices.length}</span>
      </div>
      {invoices.length > 0 ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {invoices.slice(0, 3).map((r) => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--ink-3)', width: 70 }}>{r.invoiceNumber}</span>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.account?.name ?? '—'}</span>
                {r.daysOverdue != null && <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{r.daysOverdue}d</span>}
                <span className="num-display" style={{ fontSize: 13, width: 60, textAlign: 'right' }}>{formatINRCompact(r.total ?? 0)}</span>
              </div>
            ))}
          </div>
          {invoices.length > 3 && (
            // Apr 27 (audit P1) — navigate to filtered Invoices list.
            <a
              href="/invoices?status=overdue"
              className="btn btn-ghost btn-sm"
              style={{
                marginTop: 10,
                width: '100%',
                fontSize: 12,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              View all {invoices.length} overdue
            </a>
          )}
        </>
      ) : <NoData />}
    </WidgetShell>
  );
}

function LeadRoiWidget() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/sales/lead-source-mix').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={4}><Skeleton h={120} /></WidgetShell>;

  const sources = (data?.breakdown ?? []) as Array<{ label: string; value: number }>;
  const colors = ['var(--saffron)', 'var(--forest)', 'var(--info)', '#A89A7E', '#C67C1B'];

  return (
    <WidgetShell cols={4}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Lead source mix</div>
      {sources.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sources.slice(0, 5).map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: colors[i % colors.length] }} />
              <span style={{ flex: 1 }}>{r.label}</span>
              <span className="num-display" style={{ fontSize: 13, width: 46, textAlign: 'right' }}>{formatNum(r.value)}</span>
            </div>
          ))}
        </div>
      ) : <NoData />}
    </WidgetShell>
  );
}

function AiCreditsWidget() {
  const [balance, setBalance] = useState<number | null>(null);
  const [plan, setPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Apr 23, 2026 (Bug 4) — read planTier from tenant store first (authoritative),
  // only fall back to /credits/balance.plan if tenant didn't provide it.
  const { tenant } = useAuthStore();

  useEffect(() => {
    safeGet<{ balance?: number; plan?: string }>('/credits/balance').then((r) => {
      setBalance(r.data?.balance ?? null);
      setPlan(tenant?.planTier ?? r.data?.plan ?? null);
      setLoading(false);
    });
  }, [tenant?.planTier]);

  // Plan allocations from strategy doc — approximate for progress %
  const planTotals: Record<string, number> = { FREE: 50, STARTER: 500, GROWTH: 2000, ENTERPRISE: 10000 };
  const total = plan ? (planTotals[plan.toUpperCase()] ?? 5000) : 5000;
  const used = balance != null ? Math.max(0, total - balance) : 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <WidgetShell cols={4}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>AI credits · this month</div>
      {loading ? <Skeleton h={80} /> : balance != null ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div className="num-display" style={{ fontSize: 28 }}>{formatNum(used)}</div>
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>of {formatNum(total)}</span>
          </div>
          <Progress value={pct} color="var(--saffron)" h={4} />
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            {formatNum(balance)} credits remaining · plan {plan ?? '—'}
          </div>
        </>
      ) : <NoData />}
    </WidgetShell>
  );
}

function ExportShipmentsWidget() {
  const [shipments, setShipments] = useState<Array<Record<string, unknown>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<{ shipments?: unknown[]; items?: unknown[] }>('/logistics/shipments?status=in-transit&limit=6').then((r) => {
      const list = (r.data?.shipments ?? r.data?.items ?? r.data) as unknown as Array<Record<string, unknown>> | null;
      setShipments(Array.isArray(list) ? list : null);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={6}><Skeleton h={120} /></WidgetShell>;

  return (
    <WidgetShell cols={6}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div className="kpi-label">Export shipments in transit</div>
        <div style={{ flex: 1 }} />
        <span className="chip">{shipments?.length ?? 0} active</span>
      </div>
      {shipments && shipments.length > 0 ? (
        <table className="tbl" style={{ fontSize: 12, width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Shipment</th>
              <th style={{ textAlign: 'left' }}>Destination</th>
              <th style={{ textAlign: 'left' }}>ETA</th>
              <th style={{ textAlign: 'right' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {shipments.slice(0, 5).map((r, i) => (
              <tr key={i}>
                <td style={{ fontFamily: 'monospace' }}>{String(r.shipmentNumber ?? r.id ?? '—').slice(0, 10)}</td>
                <td>{String(r.destinationPort ?? r.destinationCountry ?? r.destination ?? '—')}</td>
                <td className="num">{r.eta ? new Date(String(r.eta)).toLocaleDateString('en-IN') : '—'}</td>
                <td className="num" style={{ fontWeight: 500, textAlign: 'right' }}>{formatINRCompact(Number(r.totalValue ?? r.value ?? 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <NoData />}
    </WidgetShell>
  );
}

function DeptPerformanceWidget() {
  const [data, setData] = useState<KpiPayload | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    safeGet<KpiPayload>('/analytics/sales/rep-leaderboard').then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <WidgetShell cols={3}><Skeleton h={140} /></WidgetShell>;

  const rows = (data?.breakdown ?? []).slice(0, 4) as Array<{ label: string; value: number }>;
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;

  return (
    <WidgetShell cols={3}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Top performers</div>
      {rows.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r, i) => {
            const pct = (r.value / max) * 100;
            return (
              <div key={i}>
                <div style={{ display: 'flex', fontSize: 11, marginBottom: 4 }}>
                  <span style={{ flex: 1, color: 'var(--ink-2)' }}>{r.label}</span>
                  <span className="num" style={{ color: 'var(--ink-3)' }}>{formatINRCompact(r.value)}</span>
                </div>
                <Progress value={pct} color={pct >= 85 ? 'var(--forest)' : pct >= 70 ? 'var(--saffron)' : '#C67C1B'} h={4} />
              </div>
            );
          })}
        </div>
      ) : <NoData />}
    </WidgetShell>
  );
}

// ─── Widget registry for the Customize panel ──────────────────────────
// Apr 23, 2026 (Agent 4) — wires the previously-inert "Customize" button.
// Visibility is persisted per-user in localStorage; all 12 widgets default
// to visible (preserves existing behaviour for anyone who hasn't opened
// the panel yet).

interface WidgetDef {
  id: string;
  label: string;
  /**
   * Apr 24, 2026 (TM) — widgets may now optionally consume the dashboard's
   * active `dateRange`. Widgets that don't need it can keep their signature
   * as `() => JSX.Element`; widgets that do care should accept a single
   * `{ dateRange }` arg. No existing widget is coupled to the filter yet —
   * the prop is plumbed through as groundwork for Agent 4 to wire per
   * widget in a later batch.
   */
  render: (ctx: { dateRange: AdminDashboardDateRange }) => JSX.Element;
}

const ADMIN_WIDGETS: WidgetDef[] = [
  { id: 'revenue-mtd', label: 'Revenue MTD', render: () => <RevenueMtdWidget /> },
  { id: 'pipeline-value', label: 'Pipeline Value', render: () => <PipelineValueWidget /> },
  { id: 'cash-position', label: 'Cash Position', render: () => <CashPositionWidget /> },
  { id: 'ar-aging', label: 'AR Aging', render: () => <ArAgingWidget /> },
  { id: 'top-deals', label: 'Top Deals', render: () => <TopDealsWidget /> },
  { id: 'so-po-donut', label: 'SO/PO Mix', render: () => <SoPoDonutWidget /> },
  { id: 'dept-performance', label: 'Department Performance', render: () => <DeptPerformanceWidget /> },
  { id: 'ai-summary', label: 'AI Summary', render: () => <AiSummaryWidget /> },
  { id: 'overdue-invoices', label: 'Overdue Invoices', render: () => <OverdueInvoicesWidget /> },
  { id: 'lead-roi', label: 'Lead ROI', render: () => <LeadRoiWidget /> },
  { id: 'ai-credits', label: 'AI Credits', render: () => <AiCreditsWidget /> },
  { id: 'export-shipments', label: 'Export Shipments', render: () => <ExportShipmentsWidget /> },
];

const CUSTOMIZE_STORAGE_KEY = 'aura-admin-dashboard-visible-widgets';

function loadVisibleIds(): Set<string> {
  if (typeof window === 'undefined') return new Set(ADMIN_WIDGETS.map(w => w.id));
  try {
    const raw = window.localStorage.getItem(CUSTOMIZE_STORAGE_KEY);
    if (!raw) return new Set(ADMIN_WIDGETS.map(w => w.id));
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(ADMIN_WIDGETS.map(w => w.id));
    return new Set(parsed);
  } catch {
    return new Set(ADMIN_WIDGETS.map(w => w.id));
  }
}

function saveVisibleIds(ids: Set<string>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CUSTOMIZE_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* quota / private-mode — silently ignore */
  }
}

// ─── Customize panel (slide-over on right) ───────────────────────────────

interface CustomizePanelProps {
  open: boolean;
  onClose: () => void;
  visibleIds: Set<string>;
  onToggle: (id: string) => void;
  onReset: () => void;
}

function CustomizePanel({ open, onClose, visibleIds, onToggle, onReset }: CustomizePanelProps) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="customize-panel"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'flex-end',
        background: 'rgba(0,0,0,0.3)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 360,
          maxWidth: '100vw',
          height: '100%',
          background: 'var(--ivory-0)',
          borderLeft: '1px solid var(--line)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
        }}
      >
        {/* Panel header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--line)',
        }}>
          <div>
            <div style={{ fontFamily: 'var(--ff-serif)', fontSize: 18, color: 'var(--ink-0)' }}>
              Customize dashboard
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
              Show or hide widgets — saved for this device.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              padding: 6, borderRadius: 6, color: 'var(--ink-2)',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Widget list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
          {ADMIN_WIDGETS.map((w) => {
            const on = visibleIds.has(w.id);
            return (
              <button
                key={w.id}
                onClick={() => onToggle(w.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', margin: '2px 0',
                  border: '1px solid transparent', borderRadius: 8,
                  background: on ? 'color-mix(in srgb, var(--saffron) 8%, transparent)' : 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 18, height: 18, borderRadius: 4,
                    border: `1.5px solid ${on ? 'var(--saffron)' : 'var(--line)'}`,
                    background: on ? 'var(--saffron)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', flexShrink: 0,
                  }}
                >
                  {on && <Check size={12} strokeWidth={3} />}
                </div>
                <span style={{ fontSize: 13, color: 'var(--ink-1)' }}>{w.label}</span>
              </button>
            );
          })}
        </div>

        {/* Footer — BUG-01 fix (May 1 2026): added "Done" button; layout is auto-saved
            on every toggle so Done just calls onClose (same as X). */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button
            onClick={onReset}
            className="btn btn-outline"
            style={{ fontSize: 12, padding: '6px 12px' }}
          >
            Reset to default
          </button>
          <button
            onClick={onClose}
            style={{
              fontSize: 13, fontWeight: 600, padding: '7px 18px',
              borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'var(--ink-0)', color: 'var(--ivory-0)',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────

export function AdminDashboard() {
  const { user } = useAuthStore();
  const firstName = user?.firstName ?? 'there';
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Apr 23, 2026 (Agent 4) — Customize panel state. Previously the button had
  // no onClick at all, so QA flagged it as dead. Now it toggles widget
  // visibility via localStorage. Zero backend changes.
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [visibleIds, setVisibleIds] = useState<Set<string>>(() => loadVisibleIds());

  // Apr 24, 2026 (TM) — date-range dropdown state + click-outside close
  const [dateRange, setDateRange] = useState<AdminDashboardDateRange>(() => loadDateRange());
  const [dateOpen, setDateOpen] = useState(false);
  const dateBtnRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!dateOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!dateBtnRef.current) return;
      if (!dateBtnRef.current.contains(e.target as Node)) setDateOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDateOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [dateOpen]);

  const selectRange = (r: AdminDashboardDateRange) => {
    if (r === 'custom') {
      // Apr 27 (audit P1) — replaced alert() with native date inputs prompt.
      // Lightweight, no library. Persists to localStorage as ISO range so
      // widgets that consume `dateRange` can read `aura-admin-custom-range`.
      setDateOpen(false);
      const start = window.prompt('Custom range — start date (YYYY-MM-DD)');
      if (!start) return;
      const end = window.prompt('Custom range — end date (YYYY-MM-DD)', start);
      if (!end) return;
      try {
        window.localStorage.setItem(
          'aura-admin-custom-range',
          JSON.stringify({ start, end }),
        );
      } catch {
        /* no-op — best effort */
      }
      setDateRange('custom');
      saveDateRange('custom');
      return;
    }
    setDateRange(r);
    saveDateRange(r);
    setDateOpen(false);
  };

  const toggleWidget = (id: string) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveVisibleIds(next);
      return next;
    });
  };

  const resetToDefault = () => {
    const all = new Set(ADMIN_WIDGETS.map(w => w.id));
    setVisibleIds(all);
    saveVisibleIds(all);
  };

  const visibleWidgets = ADMIN_WIDGETS.filter((w) => visibleIds.has(w.id));

  return (
    <div className="aura-root" style={{ padding: 24, background: 'var(--ivory-0)', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 22, fontWeight: 500, marginBottom: 2, color: 'var(--ink-0)' }}>
            Good morning, {firstName}
          </h2>
          <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{dateStr}</div>
        </div>
        <div style={{ flex: 1 }} />

        {/* Date-range dropdown — wires the previously-inert "This month" button. */}
        <div ref={dateBtnRef} style={{ position: 'relative' }}>
          <button
            className="btn btn-outline"
            onClick={() => setDateOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={dateOpen}
            aria-label={`Filter by date: ${labelForRange(dateRange)}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Calendar size={13} /> {labelForRange(dateRange)}
            <ChevronDown size={11} style={{ opacity: 0.6 }} />
          </button>
          {dateOpen && (
            <div
              role="menu"
              className="status-menu"
              style={{
                top: 'calc(100% + 4px)',
                right: 0,
                minWidth: 180,
              }}
            >
              {DATE_RANGE_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  role="menuitemradio"
                  aria-checked={dateRange === o.id}
                  onClick={() => selectRange(o.id)}
                  type="button"
                >
                  <span style={{ flex: 1 }}>{o.label}</span>
                  {dateRange === o.id && <Check size={13} style={{ color: 'var(--forest-600)' }} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          className="btn btn-outline"
          onClick={() => setCustomizeOpen(true)}
          aria-label="Customize dashboard"
          style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={13} /> Customize
        </button>
        <button
          className="btn btn-outline"
          onClick={() => {
            // Apr 27, 2026 (Kunwar bug) — wire Export PDF.
            // Toggle a dashboard-only print mode that hides app chrome
            // (sidebar/topbar/AI panel) so the dashboard prints clean.
            // The user's browser "Save as PDF" destination produces the PDF —
            // no library required, matches the EndOfShiftReportPage pattern.
            try {
              document.body.classList.add('aura-print-dashboard');
              setTimeout(() => {
                window.print();
                document.body.classList.remove('aura-print-dashboard');
              }, 50);
            } catch {
              window.print();
            }
          }}
          aria-label="Export dashboard as PDF (uses browser Save as PDF)"
          title="Export PDF — choose ‘Save as PDF’ in the print dialog"
          style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Download size={13} /> Export PDF
        </button>
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 16 }}>
        {visibleWidgets.length > 0 ? (
          visibleWidgets.map((w) => <div key={w.id} style={{ display: 'contents' }}>{w.render({ dateRange })}</div>)
        ) : (
          <div style={{
            gridColumn: '1 / -1', padding: 32, textAlign: 'center',
            border: '1px dashed var(--line)', borderRadius: 10, color: 'var(--ink-3)',
          }}>
            <div style={{ fontSize: 14, marginBottom: 8 }}>No widgets enabled.</div>
            <button
              className="btn btn-outline"
              onClick={() => setCustomizeOpen(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Plus size={13} /> Open customize panel
            </button>
          </div>
        )}
      </div>

      <CustomizePanel
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        visibleIds={visibleIds}
        onToggle={toggleWidget}
        onReset={resetToDefault}
      />
    </div>
  );
}

export default AdminDashboard;
